import { useCallback, useRef, useState } from 'react';

import { ModalBody, ModalWrapper } from '@zooplus/ui-components';

import RetryPaymentCreditCardModalError from '../RetryPaymentCreditCardModalError/RetryPaymentCreditCardModalError';

import BraintreeCreditCardForm, {
  BraintreeCreditCardFormHandle,
} from '@components/BraintreeCreditCardForm/BraintreeCreditCardForm';
import ModalButtons from '@components/ModalButtons/ModalButtons';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { authorizePayment } from '@services/braintreeService';
import { initiateCreditCardPayment } from '@services/paymentService';
import { captureException } from '@services/sentryService';
import { FraudPreventionRecommendation } from '@types-internal';
import { TrackingFn } from '@types-internal/TrackingTypes';
import { convertMajorUnitsToMinorUnits } from '@utils/utils';

type RetryPaymentBraintreeModalProps = {
  show: boolean;
  onClose: () => void;
  token: string;
  amount: number;
  orderId: string;
  currency: string;
  siteId: number;
  customerId: string | undefined;
  fraudPreventionRecommendation?: FraudPreventionRecommendation;
  track?: TrackingFn;
};

const RetryPaymentBraintreeModal: React.FC<RetryPaymentBraintreeModalProps> = ({
  show,
  onClose,
  token,
  amount,
  orderId,
  currency,
  siteId,
  track,
  customerId,
  fraudPreventionRecommendation,
}) => {
  const { t } = useTranslation();
  const { csrfToken } = useAppContext();

  const creditCardFormHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { optimizely } = useConfig().config;
  const optimizelyId = String(optimizely);
  const handleCancel = useCallback(() => {
    onClose();
    setErrorMessage(undefined);
    track?.('Click_CloseModal');
  }, [onClose]);

  const handleAuthorize = async () => {
    setIsLoading(true);
    setErrorMessage(undefined);

    try {
      if (!creditCardFormHandle.current) {
        captureException({ message: 'braintree_retry_form_handle_not_found' });
        setShowErrorModal(true);
        return;
      }

      const tokenizeCardResult = await creditCardFormHandle.current.tokenizeCard();
      if (!tokenizeCardResult.success) {
        setErrorMessage(tokenizeCardResult.errorMessage);
        return;
      }

      const bin = tokenizeCardResult.data.details?.bin;
      const nonce = tokenizeCardResult.data.nonce;
      const authorizeCardResult = await authorizePayment({
        t,
        token,
        bin,
        nonce,
        amount,
        /* only one time payments during the retry */
        cardType: 'ONE_TIME',
        /* cannot create an autoshipment during the retry */
        isSubscriptionFirst: false,
        subscriptionFirstInterval: '0',
        customerId: customerId,
        fraudPreventionRecommendation,
        siteId: siteId,
        optimizelyId,
        track,
      });

      if (!authorizeCardResult.success) {
        setErrorMessage(authorizeCardResult.errorMessage);
        return;
      }

      try {
        const response = await initiateCreditCardPayment({
          csrfToken: csrfToken as string,
          paymentAmount: convertMajorUnitsToMinorUnits(amount),
          paymentMethodNonce: authorizeCardResult.nonce,
          orderId,
          siteId,
          currency,
          connector: 'BRAINTREE',
          deviceData: authorizeCardResult.deviceData,
        });
        const retryPaymentStatus = response.data;

        switch (retryPaymentStatus) {
          case 'SUCCESSFUL':
            onClose();
            window.location.replace(
              window.location.origin + window.location.pathname + '?auth=SUCCESSFUL',
            );
            break;
          case 'UNSUCCESSFUL':
          case 'CANCELLED':
            setErrorMessage(t('checkout.payment.creditcard.error.authorize.retry'));
            break;
          default:
            // Redirect to order overview page
            window.location.replace(window.location.origin + window.location.pathname);
            break;
        }
      } catch (error) {
        setShowErrorModal(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFatalError = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      <ModalWrapper
        size="md"
        id="Click_CloseModal"
        isOpen={show && !showErrorModal}
        onClose={handleCancel}
        heading={t('checkout.payment.by.card')}
        headingId="retry-payment-braintree-card"
        closeBtnAriaLabel="Close"
        data-zta="retry-payment-modal">
        <ModalBody>
          {show && !showErrorModal && (
            <BraintreeCreditCardForm
              token={token}
              error={errorMessage}
              ref={creditCardFormHandle}
              containerType="default"
              labelsInline={false}
              /* cannot save card during the retry */
              allowVaulting={false}
              onFatalError={handleFatalError}
            />
          )}
        </ModalBody>
        <ModalButtons
          handleCancel={handleCancel}
          handleAuthorize={handleAuthorize}
          isLoading={isLoading}
        />
      </ModalWrapper>
      <RetryPaymentCreditCardModalError show={showErrorModal} connector={'BRAINTREE'} />
    </>
  );
};

export default RetryPaymentBraintreeModal;
